import { InputField } from "@risalabs_frontend_org/oasis-ui-kit";
import { useRef } from "react";
import { Modal } from "risa-oasis-ui_v2";
import { MfaConfigFieldset } from "./components/MfaConfigFieldset";
import { PasswordField } from "./components/PasswordField";
import { PortalIdField } from "./components/PortalIdField";
import { YesNoRadio } from "./components/YesNoRadio";
import { MODAL_DIALOG_ID } from "./constants";
import { useCredentialForm } from "./hooks/useCredentialForm";
import { useCredentialSave } from "./hooks/useCredentialSave";
import { useEmailOptions } from "./hooks/useEmailOptions";
import { useFieldValidation } from "./hooks/useFieldValidation";
import { useMfaBranch } from "./hooks/useMfaBranch";
import { useModalLifecycle } from "./hooks/useModalLifecycle";
import { useTouched } from "./hooks/useTouched";
import { CredentialDetailsModalProps } from "./types";

/**
 * Orchestrates the credential add/edit modal. Owns nothing of its own
 * beyond the SearchableCard portal ref — every piece of state, every
 * side effect, and every validation/save concern lives in a dedicated
 * hook (see `./hooks/`) or a presentational sub-component (see
 * `./components/`). This file's only job is to wire them together.
 *
 * Hook dependency order (matters because some hooks consume the outputs
 * of earlier ones):
 *   1. useCredentialForm        — form state, password/secret-key editing,
 *                                  encrypted-value fetch in edit mode.
 *   2. useModalLifecycle        — open/close the global dialog registry.
 *   3. useEmailOptions          — Firestore OTP-inbox dropdown options.
 *   4. useTouched               — per-field touched map (shared by 5 + 6).
 *   5. useMfaBranch             — MFA branch state + branch-switch handler
 *                                  that needs touched callbacks from (4).
 *   6. useFieldValidation       — error memo + helpers, needs `mfaMethod`
 *                                  from (5) and touched map from (4).
 *   7. useCredentialSave        — encrypt + POST, needs `markAllTouched`
 *                                  from (6) and the form submission
 *                                  helpers from (1).
 */
export const CredentialDetailsModal = ({
  isOpen,
  onClose,
  isEditMode = false,
  credentialData = null,
  clientId,
  client_Name,
}: CredentialDetailsModalProps) => {
  const portalTarget = useRef<HTMLDivElement>(null);

  const {
    formData,
    isPasswordEditing,
    isSecretKeyEditing,
    handleInputChange,
    handlePasswordEditClick,
    handleSecretKeyEditClick,
    getPasswordForSubmission,
    getSecretKeyForSubmission,
  } = useCredentialForm({ isEditMode, credentialData, isOpen });

  useModalLifecycle(isOpen);

  const { modalEmailOptions } = useEmailOptions({ isOpen, isEditMode });

  const { touched, markTouched, clearTouched, markFieldsTouched } = useTouched({
    isOpen,
    isEditMode,
    credentialData,
  });

  const { mfaMethod, handleMfaMethodChange } = useMfaBranch({
    isOpen,
    isEditMode,
    credentialData,
    handleInputChange,
    markTouched,
    clearTouched,
  });

  const { hasErrors, errorFor, markAllTouched, handleFieldChange } =
    useFieldValidation({
      formData,
      isEditMode,
      isPasswordEditing,
      isSecretKeyEditing,
      mfaMethod,
      touched,
      markTouched,
      markFieldsTouched,
      handleInputChange,
    });

  const { isSaving, handleSave } = useCredentialSave({
    formData,
    hasErrors,
    markAllTouched,
    getPasswordForSubmission,
    getSecretKeyForSubmission,
    isEditMode,
    credentialData,
    clientId,
    client_Name,
    onClose,
  });

  return (
    <Modal
      dialogId={MODAL_DIALOG_ID}
      title={isEditMode ? "Edit Credential Details" : "Add New Credential"}
      onClose={onClose}
      onSave={handleSave}
      saveButtonText={isSaving ? "Saving..." : "Save"}
      cancelText="Cancel"
      heightPercentage={70}
      disableSave={isSaving || hasErrors}
    >
      <div className="flex flex-col gap-2">
        <PortalIdField
          portalTarget={portalTarget}
          portalId={formData.portal_id}
          portalIdError={errorFor("portal_id")}
          onPortalIdChange={(value) => handleFieldChange("portal_id", value)}
          onPortalIdBlur={() => markTouched("portal_id")}
          facilityName={formData.facility_name}
          facilityError={errorFor("facility_name")}
          onFacilityChange={(value) =>
            handleFieldChange("facility_name", value)
          }
          onFacilityBlur={() => markTouched("facility_name")}
          locationId={formData.location_id}
          locationError={errorFor("location_id")}
          onLocationChange={(value) => handleFieldChange("location_id", value)}
          onLocationBlur={() => markTouched("location_id")}
        />
        <InputField
          label="Portal Name"
          size="md"
          fullWidth
          required
          hint="Human-readable name of the portal as it should appear in the UI."
          error={errorFor("portal_name")}
          defaultValue={formData.portal_name}
          id="portal_name"
          onChange={(e) => handleFieldChange("portal_name", e.target.value)}
          onBlur={() => markTouched("portal_name")}
        />
        <InputField
          label="Portal URL"
          size="md"
          fullWidth
          required
          hint="Full login URL for the portal (must start with https://)."
          error={errorFor("portal_url")}
          defaultValue={formData.portal_url}
          id="portal_url"
          onChange={(e) => handleFieldChange("portal_url", e.target.value)}
          onBlur={() => markTouched("portal_url")}
        />
        <InputField
          label="Username"
          size="md"
          fullWidth
          required
          hint="Username or login ID used to sign in to the portal."
          error={errorFor("username")}
          defaultValue={formData.username}
          id="username"
          onChange={(e) => handleFieldChange("username", e.target.value)}
          onBlur={() => markTouched("username")}
        />
        <PasswordField
          error={errorFor("password")}
          isEditMode={isEditMode}
          isEditing={isPasswordEditing}
          onEditClick={() => {
            handlePasswordEditClick();
            markTouched("password");
          }}
          onChange={(value) => handleFieldChange("password", value)}
          onBlur={() => markTouched("password")}
        />
        <MfaConfigFieldset
          mfaPresent={formData.mfa_present}
          mfaPresentError={errorFor("mfa_present")}
          onMfaPresentChange={(value) => {
            handleInputChange("mfa_present", value);
            markTouched("mfa_present");
          }}
          mfaMethod={mfaMethod}
          onMfaMethodChange={handleMfaMethodChange}
          isEditMode={isEditMode}
          isSecretKeyEditing={isSecretKeyEditing}
          onSecretKeyEditClick={() => {
            handleSecretKeyEditClick();
            markTouched("secret_key");
          }}
          onSecretKeyChange={(value) => handleFieldChange("secret_key", value)}
          onSecretKeyBlur={() => markTouched("secret_key")}
          secretKeyError={errorFor("secret_key")}
          email={formData.email}
          emailError={errorFor("email")}
          onEmailChange={(value) => {
            handleInputChange("email", value);
            markTouched("email");
          }}
          modalEmailOptions={modalEmailOptions}
          emailSelectKey={`email-${credentialData?.credential_id ?? "new"}-${formData.email}-${modalEmailOptions.length}`}
          maxConcurrency={formData.max_concurrent}
          maxConcurrencyError={errorFor("max_concurrent")}
          onMaxConcurrencyChange={(value) =>
            handleFieldChange("max_concurrent", value)
          }
          onMaxConcurrencyBlur={() => markTouched("max_concurrent")}
        />
        <YesNoRadio
          label="Is Active"
          name="is_active"
          value={formData.is_active}
          error={errorFor("is_active")}
          onChange={(value) => {
            handleInputChange("is_active", value);
            markTouched("is_active");
          }}
        />
      </div>
    </Modal>
  );
};
